import React from 'react';
import { useTranslation } from "react-i18next";
import {
  Target, Eye, Award, ShieldCheck,
  Microscope, Globe, ArrowRight
} from 'lucide-react';

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-fecos-bg min-h-screen text-fecos-text selection:bg-fecos-accent/20 selection:text-fecos-text">

      {/* 1. Minimal Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 border-b border-fecos-border/50">
        <div className="max-w-[1000px] mx-auto text-center">
          <span className="text-fecos-accent font-bold text-xs tracking-[0.2em] uppercase mb-6 block animate-in fade-in slide-in-from-bottom-2">
            {t("about.headerTitle")}
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8 text-fecos-text animate-in fade-in slide-in-from-bottom-3 leading-tight">
            {t("about.mainTitle")}
          </h1>
          <p className="text-fecos-gray text-lg md:text-xl font-medium max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4">
            {t("about.mainText")}
          </p>
        </div>
      </section>

      {/* 2. Mission & Vision Grid */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Mission */}
          <div className="group p-8 md:p-12 rounded-[2rem] bg-white border border-fecos-border/50 transition-all hover:shadow-xl hover:border-fecos-accent/20">
            <div className="w-12 h-12 bg-fecos-bg rounded-xl flex items-center justify-center text-fecos-accent mb-8 group-hover:scale-110 transition-transform">
              <Target className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-fecos-text">{t("about.mission.title")}</h3>
            <p className="text-fecos-text/70 leading-relaxed text-lg">
              {t("about.mission.text")}
            </p>
          </div>

          {/* Vision */}
          <div className="group p-8 md:p-12 rounded-[2rem] bg-fecos-text text-white transition-all hover:shadow-xl hover:shadow-fecos-text/20">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-white mb-8 group-hover:scale-110 transition-transform">
              <Eye className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold mb-4">{t("about.vision.title")}</h3>
            <p className="text-gray-400 leading-relaxed text-lg">
              {t("about.vision.text")}
            </p>
          </div>
        </div>
      </section>

      {/* 3. Who We Are (Text Focused) */}
      <section className="py-20 bg-white border-y border-fecos-border/30">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8 bg-fecos-accent"></div>
            <span className="text-fecos-accent font-bold text-xs tracking-widest uppercase">{t("about.story.est")}</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-fecos-text mb-10 leading-tight">
            {t("about.story.title")}
          </h2>

          <div className="space-y-6 text-lg md:text-xl text-fecos-text/80 leading-relaxed font-light">
            <p>
              {t("about.story.p1")}
            </p>
            <p>
              {t("about.story.p2")}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-12 mt-16 pt-12 border-t border-fecos-border/30">
            <div>
              <div className="text-4xl font-bold text-fecos-text mb-1">40+</div>
              <div className="text-xs text-fecos-gray uppercase tracking-wider font-semibold">{t("about.story.stats.formulas")}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-fecos-text mb-1">100%</div>
              <div className="text-xs text-fecos-gray uppercase tracking-wider font-semibold">{t("about.story.stats.natural")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Core Values (Minimal Grid) */}
      <section className="py-24 px-6 lg:px-12 bg-fecos-bg">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-fecos-text">{t("about.values.title")}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Globe />, title: t("about.values.innovation.title"), desc: t("about.values.innovation.desc") },
              { icon: <ShieldCheck />, title: t("about.values.transparency.title"), desc: t("about.values.transparency.desc") },
              { icon: <Microscope />, title: t("about.values.rnd.title"), desc: t("about.values.rnd.desc") },
              { icon: <Award />, title: t("about.values.quality.title"), desc: t("about.values.quality.desc") }
            ].map((val, idx) => (
              <div key={idx} className="flex flex-col items-start p-6 rounded-2xl hover:bg-white transition-colors duration-300">
                <div className="w-10 h-10 bg-fecos-surface rounded-lg flex items-center justify-center text-fecos-text mb-4 border border-fecos-border">
                  {React.cloneElement(val.icon, { size: 20 })}
                </div>
                <h4 className="font-bold text-fecos-text text-lg mb-2">{val.title}</h4>
                <p className="text-sm text-fecos-gray leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;